import { getApp, getApps, initializeApp } from "firebase/app"
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  createUserWithEmailAndPassword,
  deleteUser,
  getAuth,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  reload,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  updateProfile,
  type User,
} from "firebase/auth"

import { env } from "@/lib/env"

const firebaseApp = getApps().length ? getApp() : initializeApp(env.firebase)

export const firebaseAuth = getAuth(firebaseApp)

let restored: Promise<void> | null = null

/**
 * Waits for Firebase to finish restoring a session, then reports who is signed in.
 *
 * Firebase restores asynchronously, so `currentUser` is null for a moment on
 * every cold load whether or not anyone is signed in. Waiting for the restore
 * once is what stops that moment being mistaken for a signed-out state — the
 * same thing the mobile app does before it makes a protected call.
 *
 * Only the wait is remembered, never the user it found. Holding on to that
 * user outlived sign-out: a later call got the previous account back, minted
 * a fresh session with its token and quietly signed it in again.
 */
export async function waitForAuth(): Promise<User | null> {
  restored ??= firebaseAuth.authStateReady()
  await restored
  return firebaseAuth.currentUser
}

/**
 * Returns the current ID token, or null when nobody is signed in.
 *
 * Waits for the session to be restored first, so a null return means "nobody is
 * signed in" rather than "not yet".
 */
export async function getIdToken(forceRefresh = false): Promise<string | null> {
  const user = await waitForAuth()
  if (!user) return null
  return user.getIdToken(forceRefresh)
}

export async function signInWithEmail(email: string, password: string): Promise<User> {
  const credential = await signInWithEmailAndPassword(firebaseAuth, email, password)
  return credential.user
}

export async function signUpWithEmail(
  email: string,
  password: string,
  fullName: string,
): Promise<User> {
  const credential = await createUserWithEmailAndPassword(firebaseAuth, email, password)
  if (fullName.trim()) {
    await updateProfile(credential.user, { displayName: fullName.trim() })
    // The ID token minted at creation has no name in it, and MainApp takes the
    // name from the token. Without a refresh the account is stored nameless
    // until the token happens to expire an hour later.
    await credential.user.getIdToken(true)
  }
  return credential.user
}

/**
 * Renames the signed-in account at Firebase.
 *
 * MainApp re-reads the name from the ID token on every sign-in, so a name
 * changed only in MainApp would be put back the next time. Changing it here
 * first keeps both saying the same thing.
 */
export async function renameCurrentUser(fullName: string): Promise<void> {
  const user = await waitForAuth()
  const name = fullName.trim()
  if (!user || !name || user.displayName === name) return
  await updateProfile(user, { displayName: name })
  await user.getIdToken(true)
}

export async function signInWithGoogle(): Promise<User> {
  const provider = new GoogleAuthProvider()
  // Without this the browser silently reuses the last Google session, which is
  // confusing on shared machines.
  provider.setCustomParameters({ prompt: "select_account" })
  const credential = await signInWithPopup(firebaseAuth, provider)
  return credential.user
}

export async function signInWithApple(): Promise<User> {
  const provider = new OAuthProvider("apple.com")
  provider.addScope("email")
  provider.addScope("name")
  const credential = await signInWithPopup(firebaseAuth, provider)
  return credential.user
}

export async function sendResetEmail(email: string): Promise<void> {
  await sendPasswordResetEmail(firebaseAuth, email)
}

/**
 * Sends the signed-in person Firebase's link to confirm their address.
 *
 * The link comes back to this site's `/confirm-email`, so whoever opens it on
 * the same device is carried on rather than left on Firebase's own page.
 */
export async function sendConfirmationEmail(): Promise<void> {
  const user = await waitForAuth()
  if (!user) return
  try {
    await sendEmailVerification(user, { url: `${window.location.origin}/confirm-email` })
  } catch (error) {
    // A host Firebase does not list as authorised (a preview, a new domain)
    // refuses the way back, not the email. Sent without it, the link still
    // confirms; they return by hand.
    if ((error as { code?: string }).code !== "auth/unauthorized-continue-uri") throw error
    await sendEmailVerification(user)
  }
}

/**
 * Whether the signed-in person has confirmed their address, asked of Firebase
 * now rather than read from the session it restored.
 *
 * The ID token is refreshed with it: MainApp reads `email_verified` from the
 * token, and the one held from before the link was opened still says false.
 */
export async function emailConfirmedNow(): Promise<boolean> {
  const user = await waitForAuth()
  if (!user) return false
  await reload(user)
  if (!user.emailVerified) return false
  await user.getIdToken(true)
  return true
}

export async function signOutFirebase(): Promise<void> {
  await firebaseSignOut(firebaseAuth)
}

/** How the signed-in person proves who they are, as far as this site offers. */
export type SignInMethod = "password" | "google" | "apple"

/**
 * The way the signed-in person signs in. An account linked to more than one
 * provider is asked by the first the site offers, in the order it shows them.
 */
export function signInMethodOf(user: User | null): SignInMethod | null {
  const providers = new Set(user?.providerData.map((entry) => entry.providerId) ?? [])
  if (providers.has("google.com")) return "google"
  if (providers.has("apple.com")) return "apple"
  if (providers.has("password")) return "password"
  return null
}

/**
 * Has the signed-in person sign in again, then returns an ID token minted by
 * that sign-in.
 *
 * Deleting an account asks for one: MainApp refuses a token whose sign-in is
 * more than a few minutes old, and Firebase refuses to delete a user on one
 * either, so the same proof serves both.
 */
export async function reauthenticatedIdToken(password?: string): Promise<string> {
  const user = await waitForAuth()
  if (!user) throw new Error("Not signed in.")

  const method = signInMethodOf(user)
  if (method === "password") {
    if (!user.email || !password) throw new Error("A password is needed.")
    await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, password))
  } else if (method === "google") {
    const provider = new GoogleAuthProvider()
    provider.setCustomParameters({ prompt: "select_account" })
    await reauthenticateWithPopup(user, provider)
  } else if (method === "apple") {
    await reauthenticateWithPopup(user, new OAuthProvider("apple.com"))
  } else {
    throw new Error("This sign-in can't be confirmed here.")
  }

  return user.getIdToken(true)
}

/**
 * Removes the signed-in person's Firebase sign-in, once MainApp has deleted
 * their account. MainApp holds no Firebase admin credentials, so the device
 * that just proved who it is does this part.
 */
export async function deleteFirebaseUser(): Promise<void> {
  const user = await waitForAuth()
  if (user) await deleteUser(user)
}
